import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardHeader from "@/components/DashboardHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  BookOpen,
  CalendarDays,
  Compass,
  Lock,
  Sparkles,
  Video,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  PAYMENT_LABELS,
  STATUS_LABELS,
  canEnterCourse,
  paymentBadgeClass,
  statusBadgeClass,
  studentCourses,
} from "@/lib/enrollments";
import { getMyCalendar, isJoinable, fmtDateTime, typeBadge } from "@/lib/schedule";
import CheckoutDialog from "@/components/CheckoutDialog";
import ProgressCard from "@/components/ProgressCard";
import ForceProfileDob from "@/components/ForceProfileDob";
import ReferralsCreditsCard from "@/components/ReferralsCreditsCard";
import ParentLinkRequiredModal from "@/components/ParentLinkRequiredModal";

export default function StudentDashboard() {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loadingEnr, setLoadingEnr] = useState(true);
  const [loadingCal, setLoadingCal] = useState(true);
  const [checkout, setCheckout] = useState(null); // { enrollment, course }

  const reloadEnrollments = () =>
    studentCourses().then(setEnrollments).finally(() => setLoadingEnr(false));

  // Courses the student has paid (partial or full) + active on — eligible
  // for creating a referral link.
  const paidCourses = enrollments
    .filter(
      ({ enrollment }) =>
        enrollment.status === "active" &&
        (enrollment.payment_status === "paid" ||
          enrollment.payment_status === "partial")
    )
    .map(({ enrollment, course }) => ({
      id: course.id,
      title: course.title,
      // Frontend can't see the referral snapshot before the referral is
      // created — fall back to the default bonus values. When the modal
      // creates a referral the backend uses the true admin-set defaults.
      referrer_bonus_inr: 100,
      referred_bonus_inr: 50,
      enrollment_id: enrollment.id,
    }));

  useEffect(() => {
    reloadEnrollments();
    const from = new Date().toISOString();
    const to = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
    getMyCalendar({ from, to })
      .then((rows) =>
        setUpcoming(
          rows
            .filter((r) => r.status !== "cancelled" && r.status !== "completed")
            .slice(0, 5)
        )
      )
      .finally(() => setLoadingCal(false));
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="student-dashboard">
      <ForceProfileDob />
      <DashboardHeader subtitle="Student · My learning" />
      {/* Phase 20 — non-dismissable modal for legacy under-18 users w/o parent link */}
      <ParentLinkRequiredModal />
      <main className="mx-auto max-w-6xl px-6 py-10 space-y-6">
        <div className="flex items-center gap-3 justify-between flex-wrap">
          <div className="flex items-center gap-3">
            <BookOpen className="h-6 w-6 text-amber-600" />
            <h1 className="text-2xl font-semibold tracking-tight">
              Welcome, {user?.name}
            </h1>
          </div>
          <div className="w-full sm:w-80">
            <ProgressCard />
          </div>
        </div>

        {/* Phase 16 — Referrals & Credits */}
        <ReferralsCreditsCard
          studentName={user?.name}
          paidCourses={paidCourses}
        />

        {/* Phase 15b — Self-paced learning CTA */}
        <Card
          className="border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50/40 overflow-hidden"
          data-testid="self-paced-cta"
        >
          <CardContent className="py-5 flex items-center gap-4 flex-wrap">
            <div className="h-11 w-11 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">
                Learn anything at your pace
              </div>
              <div className="text-xs text-stone-600">
                Type a topic — we'll draft a mini-course tailored to your age,
                interests and level. AI grades your written answers instantly.
              </div>
            </div>
            <Link to="/student/learn">
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="open-learn-hub"
              >
                Open my learning <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* My Courses */}
        <section data-testid="my-courses-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-stone-600">
              My courses
            </h2>
            <Link
              to="/courses"
              className="text-xs text-stone-700 underline hover:no-underline inline-flex items-center gap-1"
              data-testid="browse-public-link"
            >
              <Compass className="h-3 w-3" /> Browse public catalog
            </Link>
          </div>
          {loadingEnr ? (
            <div className="py-6 text-center text-sm text-stone-500">Loading…</div>
          ) : enrollments.length === 0 ? (
            <Card className="border-stone-200">
              <CardContent
                className="py-10 text-center space-y-2"
                data-testid="empty-enrollments"
              >
                <Lock className="mx-auto h-6 w-6 text-stone-400" />
                <div className="text-sm text-stone-600">
                  No courses mapped yet.
                </div>
                <div className="text-xs text-stone-500 max-w-md mx-auto">
                  The admin needs to map you to a teacher's course before you can
                  enrol. Browse the public catalog to see what's on offer, then
                  ask the admin for a mapping.
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {enrollments.map(({ enrollment, course }) => {
                const enter = canEnterCourse(enrollment);
                return (
                  <Card
                    key={enrollment.id}
                    className="border-stone-200 overflow-hidden"
                    data-testid={`my-course-${course.id}`}
                  >
                    <div className="h-2 bg-amber-500" />
                    <CardContent className="py-4 space-y-3">
                      <div className="space-y-1">
                        <div className="font-medium text-sm">{course.title}</div>
                        <div className="text-xs text-stone-500">
                          {course.teacher_name} · {course.level} ·{" "}
                          {course.chapters_count} chapters
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge
                          variant="outline"
                          className={statusBadgeClass(enrollment.status)}
                        >
                          {STATUS_LABELS[enrollment.status]}
                        </Badge>
                        <Badge
                          variant="outline"
                          className={paymentBadgeClass(enrollment.payment_status)}
                        >
                          {PAYMENT_LABELS[enrollment.payment_status]}
                        </Badge>
                      </div>
                      {enter ? (
                        <Link to={`/student/courses/${course.id}`}>
                          <Button
                            className="bg-stone-900 hover:bg-stone-800 text-white w-full"
                            data-testid={`enter-course-${course.id}`}
                          >
                            Enter course <ArrowRight className="ml-1 h-3.5 w-3.5" />
                          </Button>
                        </Link>
                      ) : enrollment.status === "cancelled" ? (
                        <Button
                          variant="outline"
                          className="w-full text-stone-500"
                          disabled
                          data-testid={`cancelled-${course.id}`}
                        >
                          Cancelled
                        </Button>
                      ) : (
                        <Button
                          className="bg-amber-600 hover:bg-amber-700 text-white w-full"
                          onClick={() => setCheckout({ enrollment, course })}
                          data-testid={`complete-payment-${course.id}`}
                        >
                          <Lock className="mr-1 h-3.5 w-3.5" /> Complete payment
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </section>

        {/* Upcoming classes */}
        <Card className="border-stone-200" data-testid="upcoming-classes-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-amber-600" /> Upcoming classes
            </CardTitle>
            <Link
              to="/calendar"
              className="text-xs text-stone-700 underline hover:no-underline inline-flex items-center gap-1"
              data-testid="open-calendar-link"
            >
              Open calendar <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {loadingCal ? (
              <div className="py-6 text-center text-sm text-stone-500">Loading…</div>
            ) : upcoming.length === 0 ? (
              <div
                className="py-6 text-center text-sm text-stone-500"
                data-testid="no-upcoming"
              >
                No upcoming classes from your enrolled courses in the next 30 days.
              </div>
            ) : (
              upcoming.map((s) => {
                const joinable = isJoinable(s);
                return (
                  <div
                    key={s.id}
                    className="flex items-center justify-between gap-3 px-4 py-3 border-b border-stone-100 last:border-b-0"
                    data-testid={`upcoming-class-${s.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{s.title}</div>
                      <div className="text-xs text-stone-500">
                        {fmtDateTime(s.starts_at)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className={typeBadge(s.session_type)}>
                        {s.session_type}
                      </Badge>
                      <Link to={joinable ? `/sessions/${s.id}/live` : "/calendar"}>
                        <Button
                          size="sm"
                          disabled={!joinable}
                          className={
                            joinable
                              ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                              : ""
                          }
                          variant={joinable ? "default" : "outline"}
                          data-testid={`join-button-${s.id}`}
                        >
                          <Video className="mr-1 h-3.5 w-3.5" />
                          {joinable ? "Join" : "Soon"}
                        </Button>
                      </Link>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardContent className="py-6 flex items-start gap-3">
            <BookOpen className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <div className="text-sm font-medium">Self-serve payments</div>
              <div className="text-sm text-stone-600">
                Tap <em>Complete payment</em> on any course above to unlock it
                via Stripe (USD) or Razorpay (INR).
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Phase 5 checkout */}
      <CheckoutDialog
        open={!!checkout}
        enrollment={checkout?.enrollment}
        course={checkout?.course}
        onClose={() => setCheckout(null)}
        onPaymentComplete={() => {
          setCheckout(null);
          reloadEnrollments();
        }}
      />
    </div>
  );
}
